/**
 * HTTP wire contracts for the `/subscription` module.
 *
 * Response envelopes and list/pagination schemas for all subscription routes.
 * Domain input schemas live in their own files and are re-exported here.
 */
import { z } from 'zod';

import {
  CursorPaginationQuerySchema,
  CursorPaginationResponseSchema,
} from '../common/pagination.js';

import { InvoiceSchema, InvoiceStatusSchema } from './invoice.js';
import { ModuleActivationSchema } from './module-activation.js';
import { SubscriptionPlanSchema } from './subscription-plan.js';
import { TenantSubscriptionSchema } from './tenant-subscription.js';

// Re-export input schemas for convenient route imports.
export {
  CreateSubscriptionPlanInputSchema,
  UpdateSubscriptionPlanInputSchema,
} from './subscription-plan.js';
export { UpdateTenantSubscriptionInputSchema } from './tenant-subscription.js';
export { CreateInvoiceInputSchema, MarkInvoicePaidInputSchema } from './invoice.js';
export { ToggleModuleInputSchema } from './module-activation.js';

// ---------------------------------------------------------------------------
// Subscription plans
// ---------------------------------------------------------------------------

export const ListSubscriptionPlansQuerySchema = z.object({
  isActive: z.coerce.boolean().optional(),
});
export type ListSubscriptionPlansQuery = z.infer<typeof ListSubscriptionPlansQuerySchema>;

export const ListSubscriptionPlansResponseSchema = z.object({
  items: z.array(SubscriptionPlanSchema),
});
export type ListSubscriptionPlansResponse = z.infer<typeof ListSubscriptionPlansResponseSchema>;

export const GetSubscriptionPlanResponseSchema = z.object({ plan: SubscriptionPlanSchema });
export type GetSubscriptionPlanResponse = z.infer<typeof GetSubscriptionPlanResponseSchema>;

export const CreateSubscriptionPlanResponseSchema = z.object({ plan: SubscriptionPlanSchema });
export type CreateSubscriptionPlanResponse = z.infer<typeof CreateSubscriptionPlanResponseSchema>;

export const UpdateSubscriptionPlanResponseSchema = z.object({ plan: SubscriptionPlanSchema });
export type UpdateSubscriptionPlanResponse = z.infer<typeof UpdateSubscriptionPlanResponseSchema>;

export const DeleteSubscriptionPlanResponseSchema = z.object({
  id: z.string().uuid(),
  deletedAt: z.string().datetime({ offset: true }),
});
export type DeleteSubscriptionPlanResponse = z.infer<typeof DeleteSubscriptionPlanResponseSchema>;

// ---------------------------------------------------------------------------
// Tenant subscription
// ---------------------------------------------------------------------------

export const GetTenantSubscriptionResponseSchema = z.object({
  subscription: TenantSubscriptionSchema,
});
export type GetTenantSubscriptionResponse = z.infer<typeof GetTenantSubscriptionResponseSchema>;

export const UpdateTenantSubscriptionResponseSchema = z.object({
  subscription: TenantSubscriptionSchema,
});
export type UpdateTenantSubscriptionResponse = z.infer<
  typeof UpdateTenantSubscriptionResponseSchema
>;

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

export const ListInvoicesQuerySchema = CursorPaginationQuerySchema.extend({
  status: InvoiceStatusSchema.optional(),
});
export type ListInvoicesQuery = z.infer<typeof ListInvoicesQuerySchema>;

export const ListInvoicesResponseSchema = CursorPaginationResponseSchema(InvoiceSchema);
export type ListInvoicesResponse = z.infer<typeof ListInvoicesResponseSchema>;

export const GetInvoiceResponseSchema = z.object({ invoice: InvoiceSchema });
export type GetInvoiceResponse = z.infer<typeof GetInvoiceResponseSchema>;

export const CreateInvoiceResponseSchema = z.object({ invoice: InvoiceSchema });
export type CreateInvoiceResponse = z.infer<typeof CreateInvoiceResponseSchema>;

export const MarkInvoicePaidResponseSchema = z.object({ invoice: InvoiceSchema });
export type MarkInvoicePaidResponse = z.infer<typeof MarkInvoicePaidResponseSchema>;

// ---------------------------------------------------------------------------
// Module activations
// ---------------------------------------------------------------------------

export const ListModuleActivationsQuerySchema = z.object({
  isEnabled: z.coerce.boolean().optional(),
});
export type ListModuleActivationsQuery = z.infer<typeof ListModuleActivationsQuerySchema>;

export const ListModuleActivationsResponseSchema = z.object({
  items: z.array(ModuleActivationSchema),
});
export type ListModuleActivationsResponse = z.infer<typeof ListModuleActivationsResponseSchema>;

export const ToggleModuleResponseSchema = z.object({ activation: ModuleActivationSchema });
export type ToggleModuleResponse = z.infer<typeof ToggleModuleResponseSchema>;
